import { REFRESH_STORAGE } from "../../_config";

const CHANNEL_NAME = "dr-jobs-auth";
const MSG_REQUEST = "REQUEST_REFRESH_TOKEN";
const MSG_PROVIDE = "PROVIDE_REFRESH_TOKEN";
const MSG_FORCE_LOGOUT = "FORCE_LOGOUT";

// Unique ID for this tab — used to prevent a tab from acting on its own broadcast.
const TAB_ID = Math.random().toString(36).slice(2);

// Call once per tab at startup so it can respond to token requests from new tabs.
// Pass onForceLogout to handle a cross-tab logout signal (e.g. from AutoLogin).
// Returns a cleanup function to close the channel.
export function setupTokenBroadcastListener(onForceLogout?: () => void): () => void {
  const channel = new BroadcastChannel(CHANNEL_NAME);

  channel.onmessage = (event) => {
    if (event.data?.type === MSG_REQUEST) {
      // Check sessionStorage first; fall back to localStorage (cross-tab mirror).
      const token =
        sessionStorage.getItem(REFRESH_STORAGE) ??
        localStorage.getItem(REFRESH_STORAGE);

      if (token) {
        sessionStorage.setItem(REFRESH_STORAGE, token);
        // Do not remove from localStorage — it is intentionally mirrored there for cross-tab access.
        channel.postMessage({ type: MSG_PROVIDE, token });
      }
    }

    // Ignore FORCE_LOGOUT messages that originated from this same tab.
    if (event.data?.type === MSG_FORCE_LOGOUT && event.data.tabId !== TAB_ID) {
      onForceLogout?.();
    }
  };

  return () => channel.close();
}

// Broadcast a force-logout signal to all other open tabs (e.g. when AutoLogin boots a previous session).
// TAB_ID is included so the sending tab's own listener can safely ignore the message.
export function broadcastForceLogout(): void {
  const channel = new BroadcastChannel(CHANNEL_NAME);
  channel.postMessage({ type: MSG_FORCE_LOGOUT, tabId: TAB_ID });
  channel.close();
}

// Called by a new tab whose sessionStorage is empty.
// Broadcasts a request and resolves with the token from another tab within 500ms, or null.
export function requestTokenFromExistingTab(): Promise<string | null> {
  return new Promise((resolve) => {
    const channel = new BroadcastChannel(CHANNEL_NAME);

    const timeout = setTimeout(() => {
      channel.close();
      resolve(null);
    }, 500);

    channel.onmessage = (event) => {
      if (event.data?.type === MSG_PROVIDE && event.data.token) {
        clearTimeout(timeout);
        channel.close();
        resolve(event.data.token);
      }
    };

    channel.postMessage({ type: MSG_REQUEST });
  });
}
